"use client";

import * as React from "react";
import Image from "next/image";
import { Pencil, Check } from "lucide-react";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { ChangePasswordModal } from "./change-password-modal";
import { CancelLinkedinModal } from "./cancel-linkedin-modal";
import { ChangeEmailModal } from "./change-email-modal";
import { EditProfileModal, type ProfileValues } from "./edit-profile-modal";
import { HK_DIAL_CODE } from "@/lib/validation/auth";
import { ACCOUNT_CARD_STYLE as cardStyle } from "@/components/account/shared/card-style";

const labelStyle: React.CSSProperties = {
  fontFamily: CORE_FONT.sans,
  fontSize: "13px",
  fontWeight: 700,
  letterSpacing: "0.2px",
  color: CORE_COLORS.textPrimary,
  lineHeight: "18px",
};

const valueStyle: React.CSSProperties = {
  fontFamily: CORE_FONT.sans,
  fontSize: "15px",
  color: CORE_COLORS.textSecondary,
  lineHeight: "22px",
};

const cardHeadingStyle: React.CSSProperties = {
  fontFamily: CORE_FONT.condensed,
  fontSize: "20px",
  fontWeight: 700,
  letterSpacing: "-0.2px",
  color: CORE_COLORS.textPrimary,
  lineHeight: 1.15,
};

function EditButton({
  ariaLabel,
  onClick,
}: {
  ariaLabel: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="inline-flex items-center gap-[4px] transition-colors hover:text-[color:var(--green-dark)] focus-visible:outline-none"
      style={
        {
          fontFamily: CORE_FONT.sans,
          fontSize: "13px",
          fontWeight: 700,
          color: CORE_COLORS.brandGreen,
          lineHeight: "18px",
          letterSpacing: "0.2px",
          ["--green-dark" as string]: CORE_COLORS.brandGreenDark,
        } as React.CSSProperties
      }
    >
      <Pencil className="size-[14px]" strokeWidth={2} />
      <span>修改</span>
    </button>
  );
}

function Field({
  label,
  value,
  onEdit,
  ariaLabel,
}: {
  label: string;
  value: string;
  onEdit?: () => void;
  ariaLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-[16px]">
      <div className="min-w-0 flex-1">
        <p style={labelStyle}>{label}</p>
        <p style={valueStyle} className="truncate pt-[2px]">
          {value}
        </p>
      </div>
      {onEdit && ariaLabel && (
        <EditButton ariaLabel={ariaLabel} onClick={onEdit} />
      )}
    </div>
  );
}

const CURRENT_EMAIL = "arthurchun@hkej.com";

const INITIAL_PROFILE: ProfileValues = {
  firstName: "Arthur",
  lastName: "Chun",
  // The LOCAL number only — 8 digits, which is what the edit field accepts and
  // what the schema validates. It used to carry the dial code inline
  // ("+852 22364422"), so opening 修改個人資料 prefilled a 13-character string
  // into a field capped at 8. QA: "change phone number in the prefilled phone
  // input to a 8 digit number".
  phone: "22364422",
  industry: "傳播／出版／媒體",
  position: "",
  rank: "",
};

export function ProfileCards() {
  const [passwordOpen, setPasswordOpen] = React.useState(false);
  const [emailOpen, setEmailOpen] = React.useState(false);
  const [editProfileOpen, setEditProfileOpen] = React.useState(false);
  const [profile, setProfile] = React.useState<ProfileValues>(INITIAL_PROFILE);
  const [cancelLinkedInOpen, setCancelLinkedInOpen] = React.useState(false);
  const [linkedInConnected, setLinkedInConnected] = React.useState(false);
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    const s = new URLSearchParams(window.location.search).get("state");
    if (s === "linkedin" || s === "cancel-linkedin") {
      setLinkedInConnected(true);
    }
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ connected: boolean }>).detail;
      if (detail) setLinkedInConnected(detail.connected);
    };
    window.addEventListener("account-profile:set-linkedin", handler);
    return () =>
      window.removeEventListener("account-profile:set-linkedin", handler);
  }, []);

  return (
    <div className="flex flex-col gap-[16px]">
      <div className="flex flex-col gap-[8px] pb-[8px]">
        <h2
          className="text-[28px] lg:text-[36px]"
          style={{
            fontFamily: CORE_FONT.condensed,
            fontWeight: 700,
            letterSpacing: "-0.4px",
            color: CORE_COLORS.textPrimary,
            lineHeight: 1.1,
          }}
        >
          個人資料
        </h2>
        <p
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: "15px",
            color: CORE_COLORS.textSecondary,
            lineHeight: 1.55,
          }}
        >
          查看並管理您的個人詳細資料，以及您的職位與行業。
        </p>
      </div>

      <section style={cardStyle}>
        <div className="flex flex-col gap-[20px]">
          <h3 style={cardHeadingStyle}>登入資料</h3>
          <div className="flex flex-col gap-[16px]">
            <Field
              label="電郵"
              value={CURRENT_EMAIL}
              onEdit={() => setEmailOpen(true)}
              ariaLabel="修改電郵"
            />
            <div
              aria-hidden
              style={{ borderTop: `1px solid ${CORE_COLORS.dividerSoft}` }}
            />
            <Field
              label="密碼"
              value="********"
              onEdit={() => setPasswordOpen(true)}
              ariaLabel="更改密碼"
            />
          </div>
        </div>
      </section>

      <section style={cardStyle}>
        <div className="flex flex-col gap-[20px]">
          <div className="flex items-center justify-between">
            <h3 style={cardHeadingStyle}>你的資料</h3>
            <EditButton
              ariaLabel="修改個人資料"
              onClick={() => setEditProfileOpen(true)}
            />
          </div>

          {/* Avatar row — preview only; upload/remove are in 修改個人資料 */}
          <div className="flex items-center gap-[16px]">
            <div
              className="relative shrink-0 overflow-hidden rounded-full"
              style={{
                width: 64,
                height: 64,
                backgroundColor: CORE_COLORS.dividerLight,
                border: `1px solid ${CORE_COLORS.dividerSoft}`,
              }}
            >
              {avatarUrl ? (
                // Plain <img>: data URLs from FileReader don't play well with
                // next/image fill mode.
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={avatarUrl}
                  alt="頭像"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{
                    fontFamily: CORE_FONT.sans,
                    fontSize: 22,
                    fontWeight: 700,
                    color: CORE_COLORS.textPrimary,
                  }}
                >
                  AC
                </div>
              )}
            </div>
            <div className="flex flex-col gap-[2px]">
              <p style={labelStyle}>頭像</p>
              <p style={valueStyle} className="pt-[2px]">
                {avatarUrl ? "已上載相片" : "未上載相片"}
              </p>
            </div>
          </div>
          <div
            aria-hidden
            style={{ borderTop: `1px solid ${CORE_COLORS.dividerSoft}` }}
          />

          <div className="flex flex-col gap-[16px]">
            <div className="grid grid-cols-2 gap-[16px]">
              <div>
                <p style={labelStyle}>名字</p>
                <p style={valueStyle} className="pt-[2px]">
                  {profile.firstName || "尚未填寫"}
                </p>
              </div>
              <div>
                <p style={labelStyle}>姓氏</p>
                <p style={valueStyle} className="pt-[2px]">
                  {profile.lastName || "尚未填寫"}
                </p>
              </div>
            </div>
            <div>
              <p style={labelStyle}>電話</p>
              <p style={valueStyle} className="pt-[2px]">
                {profile.phone ? `${HK_DIAL_CODE} ${profile.phone}` : "尚未填寫"}
              </p>
            </div>
            <div>
              <p style={labelStyle}>行業</p>
              <p style={valueStyle} className="pt-[2px]">
                {profile.industry || "尚未填寫"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-[16px]">
              <div>
                <p style={labelStyle}>職位</p>
                <p style={valueStyle} className="pt-[2px]">
                  {profile.position || "尚未填寫"}
                </p>
              </div>
              <div>
                <p style={labelStyle}>職級</p>
                <p style={valueStyle} className="pt-[2px]">
                  {profile.rank || "尚未填寫"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={cardStyle}>
        <div className="flex flex-col gap-[20px]">
          <div className="flex flex-col gap-[4px]">
            <h3 style={cardHeadingStyle}>連結帳戶</h3>
            <p
              style={{
                fontFamily: CORE_FONT.sans,
                fontSize: "14px",
                color: CORE_COLORS.textSecondary,
                lineHeight: 1.55,
              }}
            >
              綁定 LinkedIn 帳戶即可解鎖留言功能。
            </p>
          </div>

          <div className="flex items-center justify-between gap-[16px]">
            <div className="flex min-w-0 items-center gap-[10px]">
              <Image
                src="/images/account-profile/linkedin.svg"
                alt="LinkedIn"
                width={24}
                height={24}
                className="size-[24px] shrink-0"
              />
              <span
                className="truncate"
                style={{
                  fontFamily: CORE_FONT.sans,
                  fontSize: "15px",
                  fontWeight: 600,
                  color: CORE_COLORS.textPrimary,
                  lineHeight: "20px",
                }}
              >
                LinkedIn
              </span>
            </div>

            {linkedInConnected ? (
              <button
                type="button"
                aria-label="取消綁定 LinkedIn"
                onClick={() => setCancelLinkedInOpen(true)}
                className="inline-flex items-center gap-[6px] transition-colors hover:bg-[color:var(--green)]/[0.05]"
                style={
                  {
                    fontFamily: CORE_FONT.sans,
                    fontSize: "13px",
                    fontWeight: 700,
                    color: CORE_COLORS.brandGreen,
                    lineHeight: "18px",
                    letterSpacing: "0.2px",
                    padding: "8px 14px",
                    border: `1px solid ${CORE_COLORS.brandGreen}`,
                    borderRadius: "3px",
                    ["--green" as string]: CORE_COLORS.brandGreen,
                  } as React.CSSProperties
                }
              >
                <Check className="size-[14px]" strokeWidth={2.4} />
                <span>已綁定</span>
              </button>
            ) : (
              <button
                type="button"
                aria-label="綁定 LinkedIn"
                onClick={() => setLinkedInConnected(true)}
                className="inline-flex items-center justify-center transition-colors hover:bg-[color:var(--green)]/[0.05]"
                style={
                  {
                    fontFamily: CORE_FONT.sans,
                    fontSize: "13px",
                    fontWeight: 700,
                    color: CORE_COLORS.brandGreen,
                    lineHeight: "18px",
                    letterSpacing: "0.2px",
                    padding: "8px 14px",
                    border: `1px solid ${CORE_COLORS.brandGreen}`,
                    borderRadius: "3px",
                    ["--green" as string]: CORE_COLORS.brandGreen,
                  } as React.CSSProperties
                }
              >
                連結
              </button>
            )}
          </div>
        </div>
      </section>

      <ChangePasswordModal open={passwordOpen} onOpenChange={setPasswordOpen} />
      <ChangeEmailModal
        open={emailOpen}
        onOpenChange={setEmailOpen}
        currentEmail={CURRENT_EMAIL}
      />
      <EditProfileModal
        open={editProfileOpen}
        onOpenChange={setEditProfileOpen}
        initialValues={profile}
        initialAvatar={avatarUrl}
        onSave={(next, nextAvatar) => {
          setProfile(next);
          setAvatarUrl(nextAvatar);
          setEditProfileOpen(false);
        }}
      />
      <CancelLinkedinModal
        open={cancelLinkedInOpen}
        onOpenChange={setCancelLinkedInOpen}
        onConfirm={() => {
          setLinkedInConnected(false);
          setCancelLinkedInOpen(false);
        }}
      />
    </div>
  );
}
